import React, { useCallback, useEffect, useState } from "react";
import i18n from "i18next";
import Visor from "../../_visor/containers/Visor";
import PluginConfigModal from "./plugin_config_modal/PluginConfigModal";
import RichMarksModal from "./rich_plugins/rich_marks_modal/RichMarksModal";
import OneClickEditModal from "./rich_plugins/oneclick_edit_modal/OneClickEditModal";
import AddNewAnimationModal from "./rich_plugins/add_new_animation_modal/AddNewAnimationModal";
import SaveAsModal from "./rich_plugins/save_as_modal/SaveAsModal";
import RatioReproduceModal from "./rich_plugins/save_as_modal/RatioReproduceModal";
import RatioReproduceJobTracker from "./rich_plugins/save_as_modal/RatioReproduceJobTracker";
import UploadTemplateModal from "./rich_plugins/upload_template_modal/UploadTemplateModal";
import AddSoundModal from "./rich_plugins/add_sound_modal/AddSoundModal";
import AddSoundNewModal from "./add_sound_modal/AddSoundModal";
import NarrationJobTracker from "./add_sound_modal/NarrationJobTracker";
import AIStudioToolbar from "./toolbar/ai_studio/AIStudioToolbar";
import FileModal from "./external_provider/file_modal/FileModal";
import AddImageModal from "./add_image_modal/AddImageModal";
import VideoModal from "./carousel/audio/VideoModal";
import InfographicSceneModal from "./infographic_scene_modal/InfographicSceneModal";
import QuestionGeneratorModal from "./question_generator_modal/QuestionGeneratorModal";
import InsufficientBalanceModal from "./common/modal/InsufficientBalanceModal";
import {
  INSUFFICIENT_BALANCE_EVENT,
  CLOSED_CREDIT_REFUSAL,
  creditRefusalState,
} from "../utils/insufficientBalance";
import {
  AI_POPUP_EVENT,
  FEATURE_REGISTRY,
  dispatchOpenAIPopup,
  setPopupDocked,
} from "./ai-chat/aiPopupGateway";
import {
  addBox,
  addBoxThunk,
  addRichMark,
  editRichMark,
  changeFinalAnimation,
  changeStartAnimation,
  changeMiddleAnimation,
  changeEndAnimation,
  saveAnimation,
  addSound,
  editSound,
  addNavItem,
  addNavItems,
  selectNavItem,
  selectIndex,
  ttsGenerateSoundFile,
  ttsGetSoundMetaFile,
  updatePluginToolbar,
  updateBox,
  importEdi,
} from "../../common/actions";
import { serialize } from "../../reducers/serializer";
import { getContentLanguageName } from "../../common/content_language";

// ExternalCatalogModal is assumed global; add import if a module exists.

const EditorModals = ({
  timelineTimeSec = 0,
  state,
  setState,
  store,
  dispatch,
  pluginToolbars,
  viewToolbars,
  boxes,
  boxSelected,
  navItems,
  navItemsIds,
  navItemSelected,
  containedViews,
  containedViewSelected,
  filesUploaded,
  globalConfig,
  uploadFunction,
  deleteFunction,
  currentViewOrContained,
  title,
  disabled,
  marks,
  soundParentId,
  uploadTemplateFunction,
  voiceProfile,
  onInfographicSceneGenerated,
  onInfographicImageGenerated,
  onInfographicImagesFromSoundGenerated,
  onMotionSceneGenerated,
  // Görsel Ekle'nin iki yeni sekmesi (3B obje / hareketli görsel) hazır bir
  // kutu döndürür; motion sonucuyla aynı şekilde uygulanır.
  onSceneVisualGenerated,
  onQuestionGenerated,
}) => {
  // Route through addBoxThunk so a newly-added video's endFrame is persisted from
  // its media duration (VideoModal adds videos via this dispatcher).
  const addBoxDispatch = (...args) => dispatch(addBoxThunk(...args));
  const addRichMarkDispatch = (...args) => dispatch(addRichMark(...args));
  const editRichMarkDispatch = (...args) => dispatch(editRichMark(...args));
  const changeFinalAnimationDispatch = (...args) => dispatch(changeFinalAnimation(...args));
  const changeStartAnimationDispatch = (...args) => dispatch(changeStartAnimation(...args));
  const changeMiddleAnimationDispatch = (...args) => dispatch(changeMiddleAnimation(...args));
  const changeEndAnimationDispatch = (...args) => dispatch(changeEndAnimation(...args));
  const saveAnimationDispatch = (...args) => dispatch(saveAnimation(...args));
  const addSoundDispatch = (value) => dispatch(addSound(soundParentId, value));
  const editSoundDispatch = (value) =>
    dispatch(editSound(soundParentId, state.addSoundIndex, value));
  const addNavItemDispatch = (...args) => dispatch(addNavItem(...args));
  const addNavItemsDispatch = (...args) => dispatch(addNavItems(...args));

  // Get soundList for current scene
  const currentViewOrContainedId = containedViewSelected || navItemSelected;
  const soundList =
    store?.getState()?.undoGroup?.present?.soundList?.[currentViewOrContainedId] || [];
  const selectNavItemDispatch = (id) => dispatch(selectNavItem(id));
  const selectIndexDispatch = (id) => dispatch(selectIndex(id));
  const ttsGenerateSoundFileDispatch = (query, callback) =>
    dispatch(ttsGenerateSoundFile(query, callback));
  const ttsGetSoundMetaFileDispatch = (query, callback) =>
    dispatch(ttsGetSoundMetaFile(query, callback));
  const updatePluginToolbarStructure = (id, structure, currentPage) =>
    dispatch(
      updatePluginToolbar(
        id,
        "main",
        "structure",
        ["url", "width", "height"],
        [structure.src, structure.width, structure.height],
        [],
        currentPage
      )
    );
  const updateBoxFromConfig = (id, toolbar, newState) =>
    dispatch(updateBox(id, "", toolbar, newState));
  const serializeImportEdi = (ediState) => dispatch(serialize(importEdi(ediState)));

  useEffect(() => {
    const handler = () => setState({ showAddVideoModal: true });
    window.addEventListener("openVideoModal", handler);
    return () => window.removeEventListener("openVideoModal", handler);
  }, [setState]);

  // Generic gateway: open whichever popup the agent asked for. The chat card
  // already stashed the payload via dispatchOpenAIPopup; here we only flip the
  // matching modal state (and its initial tab, for the sound/avatar tabs).
  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail;
      const reg = detail && FEATURE_REGISTRY[detail.feature];
      if (!reg) return;
      const patch = { [reg.stateKey]: true };
      if (reg.initialTab) patch.addSoundModalTab = reg.initialTab;
      if (detail.feature === "image") patch.onAddImageModalSelected = null;
      setState(patch);
    };
    window.addEventListener(AI_POPUP_EVENT, handler);
    return () => window.removeEventListener(AI_POPUP_EVENT, handler);
  }, [setState]);

  // FIX 1: Dock the chat rail whenever any gateway popup is open, so the chat
  // never covers a popup's Generate/Cancel buttons. ALL agent-openable modals
  // must be listed here — `showAddVideoModal` was missing, which is why the
  // video popup didn't narrow the chat. (Avatar shares showAddSoundModal.)
  useEffect(() => {
    const isAnyGatewayPopupOpen =
      state.showAddImageModal ||
      state.showAddVideoModal ||
      state.showAddSoundModal ||
      state.showInfographicSceneModal ||
      state.showImageAnimatorModal;
    setPopupDocked(!!isAnyGatewayPopupOpen);
  }, [
    state.showAddImageModal,
    state.showAddVideoModal,
    state.showAddSoundModal,
    state.showInfographicSceneModal,
    state.showImageAnimatorModal,
  ]);

  // Cleanup docked class on unmount so it doesn't linger.
  useEffect(() => {
    return () => setPopupDocked(false);
  }, []);

  // Back-compat: anything still firing the old image-only event is forwarded onto
  // the new contract so nothing in flight breaks during migration.
  useEffect(() => {
    const handler = (e) => {
      const prompt = e?.detail?.prompt;
      dispatchOpenAIPopup({ feature: "image", prompt });
    };
    window.addEventListener("aidea:openImageGenerator", handler);
    return () => window.removeEventListener("aidea:openImageGenerator", handler);
  }, []);

  // Held at this stable parent so the popup survives any child modal remount
  // (e.g. AddSoundNewModal whose `key` includes its own show state).
  const [insufficientBalance, setInsufficientBalance] = useState(CLOSED_CREDIT_REFUSAL);
  const handleInsufficientBalance = useCallback((errData) => {
    setInsufficientBalance(creditRefusalState(errData));
  }, []);

  // Bridge: any component that can't prop-drill (e.g. ImageAnimatorModal
  // rendered from EditorApp/EditorShortcuts) dispatches a window CustomEvent.
  useEffect(() => {
    const handler = (e) => handleInsufficientBalance(e.detail);
    window.addEventListener(INSUFFICIENT_BALANCE_EVENT, handler);
    return () => window.removeEventListener(INSUFFICIENT_BALANCE_EVENT, handler);
  }, [handleInsufficientBalance]);

  const currentNarration = (() => {
    const soundListByView = store.getState().undoGroup.present.soundList || {};
    const currentId = currentViewOrContained || navItemSelected;
    const sounds = soundListByView[currentId] || [];
    return sounds.find((item) => item?.narrationText);
  })();
  const currentNarrationText = currentNarration?.narrationText || "";

  return (
    <>
      <Visor
        id="visor"
        title={title}
        visorVisible={state.visorVisible}
        onVisibilityToggled={() => setState({ visorVisible: !state.visorVisible })}
        filesUploaded={store.getState().filesUploaded}
        state={{
          ...store.getState().undoGroup.present,
          filesUploaded: store.getState().filesUploaded,
          status: store.getState().status,
          animationResetTriggered: state.animationResetTriggered,
        }}
      />
      <AddImageModal
        show={state.showAddImageModal}
        titleLabel={state.addImageModalTitle}
        onImageSelected={state.onAddImageModalSelected}
        onClose={() => setState({ showAddImageModal: false })}
        onCancel={() => setState({ showAddImageModal: false })}
        narrationText={currentNarrationText}
        trainingId={state.trainingId}
        navId={currentViewOrContained || navItemSelected}
        sceneIndex={navItemsIds.indexOf(navItemSelected) + 1}
        onServerModal={(titleText, message, isSuccess) =>
          setState({
            serverModalTitle: titleText,
            serverModalMessage: message,
            serverModal: true,
            serverModalSuccess: isSuccess,
          })
        }
        onBoxAdded={addBoxDispatch}
        onSceneVisualGenerated={onSceneVisualGenerated}
        boxes={boxes}
        navItems={navItems}
        navItemSelected={navItemSelected}
        containedViews={containedViews}
        containedViewSelected={containedViewSelected}
        boxSelected={boxSelected}
        pluginToolbars={pluginToolbars}
        uploadFunction={(query, keywords, callback, percentageCallback) =>
          uploadFunction(query, keywords, callback, percentageCallback)
        }
      />
      <VideoModal
        open={state.showAddVideoModal}
        title={state.addVideoModalTitle}
        onClose={() =>
          setState({
            showAddVideoModal: false,
            addVideoImportUrl: null,
            addVideoPromptProfile: null,
            addVideoProjectId: null,
            addVideoDirect: null,
          })
        }
        onVideoSelected={state.onAddVideoModalSelected}
        importUrl={state.addVideoImportUrl}
        importPromptProfile={state.addVideoPromptProfile}
        direct={state.addVideoDirect}
        projectId={state.addVideoProjectId}
        audioUrl={currentNarration?.file || ""}
        sceneNarrationText={currentNarrationText}
        trainingId={state.trainingId}
        idealStudioTrainingId={window.idealStudioTrainingId}
        onBoxAdded={addBoxDispatch}
        boxes={boxes}
        navItems={navItems}
        navItemSelected={navItemSelected}
        containedViews={containedViews}
        containedViewSelected={containedViewSelected}
        boxSelected={boxSelected}
        pluginToolbars={pluginToolbars}
        sceneIndex={navItemsIds.indexOf(navItemSelected) + 1}
        onNavItemSelected={selectNavItemDispatch}
        uploadFunction={(query, keywords, callback, percentageCallback) =>
          uploadFunction(query, keywords, callback, percentageCallback)
        }
      />
      <AddSoundNewModal
        key={`add-sound-${state.showAddSoundModal}-${state.addSoundInitialIndex ?? ""}`}
        show={state.showAddSoundModal}
        initialTab={state.addSoundModalTab || "ai"}
        initialSoundIndex={state.addSoundInitialIndex ?? null}
        initialStartTime={timelineTimeSec}
        initialNarrationText={state.addSoundInitialText || ""}
        initialNarrationTextNavId={state.addSoundInitialTextNavId || null}
        onClose={() =>
          setState({
            showAddSoundModal: false,
            onAddSoundModalSelected: null,
            addSoundInitialIndex: null,
            addSoundInitialText: null,
            addSoundInitialTextNavId: null,
          })
        }
        onCancel={() =>
          setState({
            showAddSoundModal: false,
            onAddSoundModalSelected: null,
            addSoundInitialIndex: null,
            addSoundInitialText: null,
            addSoundInitialTextNavId: null,
          })
        }
        navItemSelected={navItemSelected}
        navItemsIds={navItemsIds}
        soundList={soundList}
        allSoundLists={store?.getState()?.undoGroup?.present?.soundList || {}}
        onAddSound={addSoundDispatch}
        onEditSound={(index, value) => dispatch(editSound(soundParentId, index, value))}
        onAddSoundForScene={(sceneId, value) => dispatch(addSound(sceneId, value))}
        onEditSoundForScene={(sceneId, index, value) => dispatch(editSound(sceneId, index, value))}
        onSoundSelected={
          state.onAddSoundModalSelected
            ? (url) => {
                state.onAddSoundModalSelected(url);
                setState({
                  showAddSoundModal: false,
                  onAddSoundModalSelected: null,
                  addSoundInitialIndex: null,
                  addSoundInitialText: null,
                  addSoundInitialTextNavId: null,
                });
              }
            : null
        }
        onServerModal={(titleText, message, isSuccess) =>
          setState({
            serverModalTitle: titleText,
            serverModalMessage: message,
            serverModal: true,
            serverModalSuccess: isSuccess,
          })
        }
        voiceProfile={voiceProfile}
        boxes={boxes}
        navItems={navItems}
        containedViews={containedViews}
        containedViewSelected={containedViewSelected}
        boxSelected={boxSelected}
        pluginToolbars={pluginToolbars}
        onBoxAdded={addBoxDispatch}
        uploadFunction={(query, keywords, callback) => uploadFunction(query, keywords, callback)}
        onInsufficientBalance={handleInsufficientBalance}
        onNavItemSelected={(id) => {
          selectNavItemDispatch(id);
          selectIndexDispatch(id);
        }}
      />
      <PluginConfigModal
        id={state.pluginConfigModal}
        fileModalResult={state.fileModalResult}
        openFileModal={(id) => {
          setState({
            fileModalResult: { id, value: undefined },
            leftPanel: "library",
            libraryCategory: "My Files",
            aiPanel: null,
          });
        }}
        name={
          pluginToolbars[state.pluginConfigModal]
            ? pluginToolbars[state.pluginConfigModal].pluginId
            : ""
        }
        state={
          pluginToolbars[state.pluginConfigModal]
            ? pluginToolbars[state.pluginConfigModal].state
            : {}
        }
        closeConfigModal={() => {
          setState({ pluginConfigModal: false });
        }}
        updatePluginToolbar={(id, newState) =>
          updateBoxFromConfig(id, pluginToolbars[state.pluginConfigModal], newState)
        }
      />
      {typeof ExternalCatalogModal !== "undefined" &&
        ExternalCatalogModal &&
        window.IdealStudio?.Config?.external_providers?.enable_catalog_modal && (
          <ExternalCatalogModal
            images={filesUploaded}
            visible={state.catalogModal}
            onExternalCatalogToggled={() => setState({ catalogModal: !state.catalogModal })}
          />
        )}
      <RichMarksModal
        boxSelected={boxSelected}
        pluginToolbar={pluginToolbars[boxSelected]}
        navItemSelected={navItemSelected}
        pluginToolbars={pluginToolbars}
        viewToolbars={viewToolbars}
        markCursorValue={state.markCursorValue}
        containedViewSelected={containedViewSelected}
        containedViews={containedViews}
        marks={marks}
        navItems={navItems}
        navItemsIds={navItemsIds}
        visible={state.richMarksVisible}
        currentRichMark={state.currentRichMark}
        defaultValueMark={
          pluginToolbars[boxSelected] &&
          pluginToolbars[boxSelected].config &&
          window.IdealStudio?.Plugins.get(pluginToolbars[boxSelected].config.name)
            ? window.IdealStudio.Plugins.get(pluginToolbars[boxSelected].config.name).getConfig()
                .defaultMarkValue
            : 0
        }
        validateValueInput={
          pluginToolbars[boxSelected] &&
          pluginToolbars[boxSelected].config &&
          window.IdealStudio?.Plugins.get(pluginToolbars[boxSelected].config.name)
            ? window.IdealStudio.Plugins.get(pluginToolbars[boxSelected].config.name)
                .validateValueInput
            : null
        }
        onBoxAdded={(
          ids,
          draggable,
          resizable,
          content,
          style,
          boxState,
          structure,
          initialParams
        ) => addBox(ids, draggable, resizable, content, style, boxState, structure, initialParams)}
        onRichMarkAdded={(mark, view, viewToolbar) => addRichMarkDispatch(mark, view, viewToolbar)}
        onRichMarkUpdated={(mark, view, viewToolbar) =>
          editRichMarkDispatch(mark, view, viewToolbar)
        }
        onRichMarksModalToggled={() => {
          setState({ richMarksVisible: !state.richMarksVisible });
          if (state.richMarksVisible) {
            setState({ currentRichMark: null, markCursorValue: null });
          }
        }}
      />
      <OneClickEditModal
        boxSelected={boxSelected}
        pluginToolbar={pluginToolbars[boxSelected]}
        navItemSelected={navItemSelected}
        pluginToolbars={pluginToolbars}
        viewToolbars={viewToolbars}
        markCursorValue={state.markCursorValue}
        containedViewSelected={containedViewSelected}
        containedViews={containedViews}
        marks={marks}
        navItems={navItems}
        navItemsIds={navItemsIds}
        visible={state.oneClickEditVisible}
        currentRichMark={state.currentRichMark}
        defaultValueMark={
          pluginToolbars[boxSelected] &&
          pluginToolbars[boxSelected].config &&
          window.IdealStudio?.Plugins.get(pluginToolbars[boxSelected].config.name)
            ? window.IdealStudio.Plugins.get(pluginToolbars[boxSelected].config.name).getConfig()
                .defaultMarkValue
            : 0
        }
        validateValueInput={
          pluginToolbars[boxSelected] &&
          pluginToolbars[boxSelected].config &&
          window.IdealStudio?.Plugins.get(pluginToolbars[boxSelected].config.name)
            ? window.IdealStudio.Plugins.get(pluginToolbars[boxSelected].config.name)
                .validateValueInput
            : null
        }
        onBoxAdded={(
          ids,
          draggable,
          resizable,
          content,
          style,
          boxState,
          structure,
          initialParams
        ) =>
          addBoxDispatch(
            ids,
            draggable,
            resizable,
            content,
            style,
            boxState,
            structure,
            initialParams
          )
        }
        onRichMarkAdded={(mark, view, viewToolbar) => addRichMarkDispatch(mark, view, viewToolbar)}
        onRichMarkUpdated={(mark, view, viewToolbar) =>
          editRichMarkDispatch(mark, view, viewToolbar)
        }
        onOneClickEditModalToggled={() => {
          setState({ oneClickEditVisible: !state.oneClickEditVisible });
          if (state.oneClickEditVisible) {
            setState({ currentRichMark: null, markCursorValue: null });
          }
        }}
      />
      <AddNewAnimationModal
        box={boxes[boxSelected]}
        boxSelected={boxSelected}
        pluginToolbar={pluginToolbars[boxSelected]}
        navItemSelected={navItemSelected}
        pluginToolbars={pluginToolbars}
        viewToolbars={viewToolbars}
        markCursorValue={state.markCursorValue}
        containedViewSelected={containedViewSelected}
        containedViews={containedViews}
        marks={marks}
        navItems={navItems}
        navItemsIds={navItemsIds}
        visible={state.addAnimationVisible}
        currentRichMark={state.currentRichMark}
        defaultValueMark={
          pluginToolbars[boxSelected] &&
          pluginToolbars[boxSelected].config &&
          window.IdealStudio?.Plugins.get(pluginToolbars[boxSelected].config.name)
            ? window.IdealStudio.Plugins.get(pluginToolbars[boxSelected].config.name).getConfig()
                .defaultMarkValue
            : 0
        }
        validateValueInput={
          pluginToolbars[boxSelected] &&
          pluginToolbars[boxSelected].config &&
          window.IdealStudio?.Plugins.get(pluginToolbars[boxSelected].config.name)
            ? window.IdealStudio.Plugins.get(pluginToolbars[boxSelected].config.name)
                .validateValueInput
            : null
        }
        onBoxAdded={(
          ids,
          draggable,
          resizable,
          content,
          style,
          boxState,
          structure,
          initialParams
        ) => addBox(ids, draggable, resizable, content, style, boxState, structure, initialParams)}
        onRichMarkAdded={(mark, view, viewToolbar) => addRichMark(mark, view, viewToolbar)}
        onRichMarkUpdated={(mark, view, viewToolbar) => editRichMark(mark, view, viewToolbar)}
        onBoxFinalAnimation={(
          id,
          firstopacity,
          middleopacity,
          lastopacity,
          firstduration,
          middleduration,
          lastduration,
          parent,
          container
        ) =>
          changeFinalAnimationDispatch(
            id,
            firstopacity,
            middleopacity,
            lastopacity,
            firstduration,
            middleduration,
            lastduration,
            parent,
            container
          )
        }
        animationById={store.getState().undoGroup.present.animationsById[boxSelected]}
        onSaveNewAnimation={(id, animations) => saveAnimationDispatch(id, animations)}
        onBoxStartAnimation={(
          id,
          firstpositionx,
          firstpositiony,
          firstwidth,
          firstheight,
          parent,
          container
        ) =>
          changeStartAnimationDispatch(
            id,
            firstpositionx,
            firstpositiony,
            firstwidth,
            firstheight,
            parent,
            container
          )
        }
        onBoxMiddleAnimation={(
          id,
          middlepositionx,
          middlepositiony,
          middlewidth,
          middleheight,
          parent,
          container
        ) =>
          changeMiddleAnimationDispatch(
            id,
            middlepositionx,
            middlepositiony,
            middlewidth,
            middleheight,
            parent,
            container
          )
        }
        onBoxEndAnimation={(
          id,
          lastpositionx,
          lastpositiony,
          lastwidth,
          lastheight,
          parent,
          container
        ) =>
          changeEndAnimationDispatch(
            id,
            lastpositionx,
            lastpositiony,
            lastwidth,
            lastheight,
            parent,
            container
          )
        }
        onAddNewAnimationModalToggled={() => {
          setState({ addAnimationVisible: !state.addAnimationVisible });
        }}
      />
      <SaveAsModal
        containedViewSelected={containedViewSelected}
        containedViews={containedViews}
        navItems={navItems}
        navItemsIds={navItemsIds}
        visible={state.saveAsVisible}
        title={globalConfig.title}
        exportedState={{ ...store.getState() }}
        preparedState={state.saveAsPreparedState || null}
        preparedTitle={state.saveAsTitleSuggestion || ""}
        initialCanvasRatio={
          // When the SaveAs chain was triggered by a ratio change, the
          // prepared state already carries the new canvasRatio in its
          // globalConfig — surface that as the modal's starting ratio so
          // the user sees no warning unless they change it again. Falls
          // back to the live globalConfig.canvasRatio for plain SaveAs.
          (state.saveAsPreparedState &&
            state.saveAsPreparedState.undoGroup &&
            state.saveAsPreparedState.undoGroup.present &&
            state.saveAsPreparedState.undoGroup.present.globalConfig &&
            state.saveAsPreparedState.undoGroup.present.globalConfig.canvasRatio) ||
          (globalConfig && globalConfig.canvasRatio) ||
          16 / 9
        }
        initialRatioKey={
          (state.saveAsPreparedState &&
            state.saveAsPreparedState.undoGroup &&
            state.saveAsPreparedState.undoGroup.present &&
            state.saveAsPreparedState.undoGroup.present.globalConfig &&
            state.saveAsPreparedState.undoGroup.present.globalConfig.ratio) ||
          (globalConfig && globalConfig.ratio) ||
          null
        }
        onSaveAsModalToggled={() => {
          setState({
            saveAsVisible: !state.saveAsVisible,
            saveAsPreparedState: null,
            saveAsTitleSuggestion: "",
          });
        }}
      />
      <RatioReproduceModal
        visible={state.ratioReproduceVisible}
        title={globalConfig.title}
        trainingId={window.idealStudioTrainingId}
        currentCanvasRatio={globalConfig && globalConfig.canvasRatio}
        language={getContentLanguageName()}
        onToggle={() => setState({ ratioReproduceVisible: false })}
      />
      <RatioReproduceJobTracker />
      <UploadTemplateModal
        containedViewSelected={containedViewSelected}
        containedViews={containedViews}
        navItems={navItems}
        navItemsIds={navItemsIds}
        visible={state.uploadTemplateVisible}
        title={globalConfig.title}
        onUploadTemplateModalToggled={() => {
          setState({ uploadTemplateVisible: !state.uploadTemplateVisible });
        }}
        uploadTemplateFunction={(value) => uploadTemplateFunction(value)}
      />
      <AddSoundModal
        containedViewSelected={containedViewSelected}
        containedViews={containedViews}
        navItems={navItems}
        navItemsIds={navItemsIds}
        visible={state.addSoundVisible}
        title={globalConfig.title}
        ttsGenerateSoundFile={(query, callback) => ttsGenerateSoundFileDispatch(query, callback)}
        ttsGetSoundMetaFile={(query, callback) => ttsGetSoundMetaFileDispatch(query, callback)}
        onServerModal={(titleText, message, isSuccess) =>
          setState({
            serverModalTitle: titleText,
            serverModalMessage: message,
            serverModalSuccess: isSuccess,
            serverModal: true,
          })
        }
        onAddSoundModalToggled={() => {
          setState({
            addSoundVisible: !state.addSoundVisible,
            addSoundIndex: -1,
            addSoundValues: null,
          });
        }}
        soundList={[
          {
            id: "file-000",
            name: "Beethoven-Symphony9.mp3",
            url: "https://cloud.aideapro.ai/middlewareblob/common/aideapro/library/sound/Beethoven-Symphony9.mp3",
          },
        ].concat(
          Object.keys(store.getState().filesUploaded)
            .map((key) => store.getState().filesUploaded[key])
            .filter((file) => file.mimetype.includes("audio"))
            .map((file) => {
              return { name: file.name, url: file.url, id: file.id };
            })
        )}
        addSound={(value) => addSoundDispatch(value)}
        editSound={(value) => editSoundDispatch(value)}
        values={
          state.addSoundValues ||
          (boxSelected !== -1 && state.addSoundIndex !== -1
            ? (store.getState().undoGroup.present.pluginToolbarsById[boxSelected] &&
                store.getState().undoGroup.present.pluginToolbarsById[boxSelected].state &&
                store.getState().undoGroup.present.pluginToolbarsById[boxSelected].state
                  .soundList &&
                store.getState().undoGroup.present.pluginToolbarsById[boxSelected].state.soundList[
                  state.addSoundIndex
                ]) ||
              null
            : state.addSoundIndex !== -1
              ? (store.getState().undoGroup.present.soundList[currentViewOrContained] &&
                  store.getState().undoGroup.present.soundList[currentViewOrContained][
                    state.addSoundIndex
                  ]) ||
                null
              : null)
        }
        uploadFunction={(query, keywords, callback) => uploadFunction(query, keywords, callback)}
        voiceProfile={voiceProfile}
      />
      <InfographicSceneModal
        show={state.showInfographicSceneModal}
        onClose={() => setState({ showInfographicSceneModal: false })}
        onCancel={() => setState({ showInfographicSceneModal: false })}
        trainingId={state.trainingId}
        navId={currentViewOrContained || navItemSelected}
        sceneIndex={navItemsIds.indexOf(navItemSelected) + 1}
        onSceneGenerated={onInfographicSceneGenerated}
        onImageGenerated={onInfographicImageGenerated}
        onImagesFromSoundGenerated={onInfographicImagesFromSoundGenerated}
        onMotionSceneGenerated={onMotionSceneGenerated}
        voiceProfile={voiceProfile}
        store={store}
        navItemSelected={navItemSelected}
        navItemsIds={navItemsIds}
        currentViewOrContained={currentViewOrContained}
      />
      {/* trainingId: widget/job satırı ve socket odası ?id= (window.idealStudioTrainingId)
          ile anahtarlanır; state.trainingId (server response.id) farklı olabilir ve iş
          widget'ta görünmezdi. Servis trainingId'yi zaten idealStudioTrainingId olarak
          kullanıyor (InfographicScene ile aynı kaynak). */}
      <QuestionGeneratorModal
        show={state.showQuestionGeneratorModal}
        onClose={() => setState({ showQuestionGeneratorModal: false })}
        onCancel={() => setState({ showQuestionGeneratorModal: false })}
        trainingId={window.idealStudioTrainingId || state.trainingId}
        navId={currentViewOrContained || navItemSelected}
        onQuestionGenerated={onQuestionGenerated}
        voiceProfile={voiceProfile}
        navItemsIds={store.getState().undoGroup.present.navItemsIds || []}
        navItemsById={store.getState().undoGroup.present.navItemsById || {}}
        pluginToolbarsById={store.getState().undoGroup.present.pluginToolbarsById || {}}
        soundListAll={store.getState().undoGroup.present.soundList || {}}
        soundList={
          store.getState().undoGroup.present.soundList &&
          store.getState().undoGroup.present.soundList[currentViewOrContained || navItemSelected]
            ? store.getState().undoGroup.present.soundList[
                currentViewOrContained || navItemSelected
              ]
            : []
        }
      />
      <AIStudioToolbar
        open={state.aiStudioOpen}
        onClose={() => setState({ aiStudioOpen: false })}
        soundList={
          store.getState().undoGroup.present.soundList &&
          store.getState().undoGroup.present.soundList[currentViewOrContained]
            ? store.getState().undoGroup.present.soundList[currentViewOrContained]
            : []
        }
        navItemSelected={navItemSelected}
        onChatSubmit={(message) => {
          // placeholder for AI integration
        }}
        dispatch={dispatch}
        boxes={boxes}
        boxSelected={boxSelected}
        navItems={navItems}
        navItemsIds={navItemsIds}
        containedViews={containedViews}
        containedViewSelected={containedViewSelected}
        globalConfig={globalConfig}
        viewToolbars={viewToolbars}
      />
      {/* TODO: deprecated FileModal — no call site sets `showFileUpload` anymore.
          Safe to remove once all flows are confirmed to use AddImageModal / AddSoundModal / VideoModal / library. */}
      {/* <FileModal
        visible={state.showFileUpload}
        disabled={disabled}
        onBoxAdded={(
          ids,
          draggable,
          resizable,
          content,
          style,
          boxState,
          structure,
          initialParams
        ) => {
          addBox(ids, draggable, resizable, content, style, boxState, structure, initialParams);
        }}
        boxSelected={boxSelected}
        boxes={boxes}
        isBusy={globalConfig.isBusy}
        importEdi={(ediState) => serializeImportEdi(ediState)}
        fileModalResult={state.fileModalResult}
        navItemsIds={navItemsIds}
        navItems={navItems}
        onNavItemSelected={(id) => selectNavItemDispatch(id)}
        containedViews={containedViews}
        containedViewSelected={containedViewSelected}
        navItemSelected={navItemSelected}
        filesUploaded={filesUploaded}
        pluginToolbars={pluginToolbars}
        marks={marks}
        deleteFileFromServer={(id, url, callback) => deleteFunction(id, url, callback)}
        onIndexSelected={(id) => selectIndexDispatch(id)}
        fileUploadTab={state.fileUploadTab}
        onNavItemAdded={(
          id,
          name,
          parent,
          type,
          position,
          background,
          customSize,
          hideTitles,
          hasContent,
          sortable_id
        ) =>
          addNavItemDispatch(
            id,
            name,
            parent,
            type,
            position,
            background,
            customSize,
            hideTitles,
            type !== "section" ||
              (type === "section" && window.IdealStudio?.Config.sections_have_content),
            sortable_id
          )
        }
        onNavItemsAdded={(navs, parent) => addNavItemsDispatch(navs, parent)}
        uploadFunction={(query, keywords, callback, percentageCallback) =>
          uploadFunction(query, keywords, callback, percentageCallback)
        }
        close={(fileModalResult) => {
          setState({
            fileModalResult: fileModalResult
              ? fileModalResult
              : { id: undefined, value: undefined },
            showFileUpload: false,
            fileUploadTab: 0,
          });

          if (fileModalResult && fileModalResult.structure && fileModalResult.id) {
            const fileData =
              store?.getState()?.undoGroup?.present?.pluginToolbarsById[fileModalResult.id];

            if (
              fileData?.pluginId === "EnrichedPlayer" ||
              fileData?.pluginId === "AudioCue" ||
              !fileData?.state?.url?.startsWith("data")
            ) {
              return;
            }

            const currentPage =
              containedViewSelected && containedViewSelected !== 0
                ? containedViewSelected
                : navItemSelected;

            updatePluginToolbarStructure(
              fileModalResult.id,
              fileModalResult.structure,
              currentPage
            );
          }
        }}
      /> */}

      <InsufficientBalanceModal
        show={insufficientBalance.show}
        onClose={() => setInsufficientBalance((prev) => ({ ...prev, show: false }))}
        requestedAmount={insufficientBalance.requestedAmount}
        currentBalance={insufficientBalance.currentBalance}
        kind={insufficientBalance.kind}
        teamName={insufficientBalance.teamName}
        resetsAt={insufficientBalance.resetsAt}
      />

      <NarrationJobTracker store={store} dispatch={dispatch} />
    </>
  );
};

export default EditorModals;
